import React, { useMemo, useState } from "react";
import styled from "styled-components";
import ProgressBar from "@/components/common/ProgressBar";
import { palette } from "@/styles/palette";

interface TastingNoteProps {
    label: string;
    value: number;
    description?: string;
    scale?: string[];
    readOnly?: boolean;
    onChange?: (value: number) => void;
}

const TASTE_DESCRIPTIONS = {
    신맛: [
        "없음",
        "오렌지(3.69-4.3) 정도",
        "사과(3.30-4.00) 정도",
        "키위(3.10-3.96) 정도",
        "자몽(2.90-3.30) 정도",
        "레몬(2.00-2.60) 정도"
    ],
    단맛: [
        "없음",
        "딸기(3-5) 정도",
        "수박/참외(9-12) 정도",
        "바나나(20-25) 정도",
        "무화과(40-75) 정도",
        "꿀(80-85) 정도"
    ]
} as const;

const TASTE_TOOLTIPS = {
    단맛: {
        title: "단맛은 이런 음식들의 달콤함과 비교해보세요",
        levels: [
            "1단계: 딸기(3-5 brix) - 살짝 달콤함",
            "2단계: 수박/참외(9-12 brix) - 적당히 달콤함",
            "3단계: 바나나(20-25 brix) - 달콤함",
            "4단계: 무화과(40-75 brix) - 매우 달콤함",
            "5단계: 꿀(80-85 brix) - 아주 강한 단맛"
        ]
    },
    신맛: {
        title: "신맛은 이런 과일들의 새콤함과 비교해보세요",
        levels: [
            "1단계: 오렌지(pH 3.69-4.3) - 살짝 신맛",
            "2단계: 사과(pH 3.30-4.00) - 적당한 신맛",
            "3단계: 키위(pH 3.10-3.96) - 확실한 신맛",
            "4단계: 자몽(pH 2.90-3.30) - 강한 신맛",
            "5단계: 레몬(pH 2.00-2.60) - 매우 강한 신맛"
        ]
    }
};

const TastingNote: React.FC<TastingNoteProps> = ({
    label,
    value,
    description: customDescription,
    scale = ["없음", "매우강함"],
    readOnly = true,
    onChange
}) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const description = useMemo(() => {
        if (label === "단맛" || label === "신맛") {
            return TASTE_DESCRIPTIONS[label][value];
        }
        return customDescription;
    }, [label, value, customDescription]);

    const tooltipContent = useMemo(() => {
        if (label === "단맛" || label === "신맛") {
            return TASTE_TOOLTIPS[label];
        }
        return { title: "", levels: [] };
    }, [label]);

    const handleChange = (newValue: number) => {
        if (onChange) {
            onChange(Math.round(newValue));
        }
    };

    return (
        <Container>
            <TitleWrapper>
                <Label>{label}</Label>
                <DescriptionWrapper>
                    {(label === "단맛" || label === "신맛") && (
                        <TooltipContainer>
                            <QuestionIcon
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            />
                            {showTooltip && (
                                <Tooltip>
                                    <TooltipTitle>{tooltipContent.title}</TooltipTitle>
                                    <LevelsList>
                                        {tooltipContent.levels.map((level, index) => (
                                            <LevelItem key={index} $highlighted={index === value - 1}>
                                                {level}
                                            </LevelItem>
                                        ))}
                                    </LevelsList>
                                </Tooltip>
                            )}
                        </TooltipContainer>
                    )}
                    <Description>
                        {description || (label === "단맛" || label === "신맛" ? TASTE_DESCRIPTIONS[label][0] : "")}
                    </Description>
                </DescriptionWrapper>
            </TitleWrapper>
            <ProgressBarContainer>
                <ProgressBar
                    value={value}
                    showSteps={true}
                    scale={scale}
                    height="8px"
                    backgroundColor="#F5F5F5"
                    progressColor={palette.primary}
                    readOnly={readOnly}
                    onChange={handleChange}
                    min={0}
                    max={5}
                    step={1}
                />
            </ProgressBarContainer>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Label = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${palette.gray[900]};

    @media screen and (min-width: 640px) {
        font-size: 16px;
    }
`;

const DescriptionWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const TooltipContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const Tooltip = styled.div`
    position: fixed;
    bottom: 20%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 16px;
    border-radius: 8px;
    width: 80%;
    max-width: 350px;
    font-size: 14px;
    line-height: 1.5;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

    @media screen and (min-width: 768px) {
        position: absolute;
        bottom: auto;
        top: -10px;
        transform: translate(-50%, -100%);
        width: 280px;

        &:after {
            content: "";
            position: absolute;
            bottom: -6px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid rgba(0, 0, 0, 0.9);
        }
    }
`;

const TooltipTitle = styled.div`
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
    color: white;
    text-align: center;
`;

const LevelsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const LevelItem = styled.li<{ $highlighted: boolean }>`
    padding: 6px 8px;
    margin-bottom: 3px;
    border-radius: 4px;
    font-size: 13px;
    background-color: ${(props) => (props.$highlighted ? "rgba(0, 127, 115, 0.3)" : "transparent")};
    font-weight: ${(props) => (props.$highlighted ? "600" : "normal")};
`;

const QuestionIcon = styled.div`
    width: 16px;
    height: 16px;
    background-image: url("/icon/question-mark-icon.png");
    background-size: contain;
    background-repeat: no-repeat;
    cursor: help;
`;

const Description = styled.div`
    font-size: 12px;
    color: ${palette.gray[600]};
    white-space: nowrap;
`;

const ProgressBarContainer = styled.div`
    width: 100%;
`;

export default TastingNote;
